import { User } from "feature/admin/types";
import React from "react";
import MoveUsersButton from "./MoveUsersButton";
import { InviteAction } from "types/types";

interface Props {
  title: string;
  isLoading: boolean;
  users: User[];
  toggleAllHandler?: () => void;
  selectedUsers?: User[];
  toggleUserSelectionHandler?: (user: User, checked: boolean) => void;
  checked?: boolean;
  checkbox?: React.RefObject<HTMLInputElement>;
  canMoveUsers?: boolean;
  isPendingUsers?: boolean;
  handleAction?: (id: string | null, action: InviteAction) => void;
  showOrganization?: boolean;
}

const UserTable = ({
  title,
  isLoading,
  users,
  toggleAllHandler,
  selectedUsers,
  toggleUserSelectionHandler,
  checkbox,
  checked,
  canMoveUsers,
  isPendingUsers,
  handleAction,
  showOrganization,
}: Props) => {
  if (isLoading) return <div>Loading...</div>;

  if (users.length === 0)
    return (
      <div>
        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
          {title}
        </h2>
        <p className="mt-4 text-sm">No users found.</p>
      </div>
    );

  return (
    <div className="flex flex-col gap-10">
      <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
        {title}
      </h2>
      {!isLoading && users.length > 0 && (
        <div className="flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="relative">
                {selectedUsers && selectedUsers.length > 0 && canMoveUsers && (
                  <div className="absolute left-14 top-0 flex h-12 items-center space-x-3 bg-white sm:left-12">
                    <MoveUsersButton users={selectedUsers} />
                  </div>
                )}
                <table className="min-w-full table-fixed divide-y divide-gray-300">
                  <thead>
                    <tr>
                      {canMoveUsers && selectedUsers && (
                        <th
                          scope="col"
                          className="relative px-7 sm:w-12 sm:px-6"
                        >
                          <input
                            type="checkbox"
                            className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            ref={checkbox}
                            checked={checked}
                            onChange={toggleAllHandler}
                          />
                        </th>
                      )}

                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Username
                      </th>
                      <th
                        scope="col"
                        className="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                      >
                        First Name
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Last Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Email
                      </th>
                      {showOrganization && (
                        <th
                          scope="col"
                          className="min-w-[12rem] px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          Organisation Name
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {users.map((user: User) => (
                      <tr key={user.id} className={"bg-gray-50"}>
                        {canMoveUsers && (
                          <td className="relative px-7 sm:w-12 sm:px-6">
                            {selectedUsers && selectedUsers.includes(user) && (
                              <div className="absolute inset-y-0 left-0 w-0.5 bg-indigo-600" />
                            )}
                            <input
                              type="checkbox"
                              className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                              value={user.email_address}
                              checked={
                                selectedUsers && selectedUsers.includes(user)
                              }
                              onChange={(e) =>
                                toggleUserSelectionHandler &&
                                toggleUserSelectionHandler(
                                  user,
                                  e.target.checked
                                )
                              }
                            />
                          </td>
                        )}

                        <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                          <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            {user.username}
                          </span>
                        </td>
                        <td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm sm:pl-0">
                          {user.firstname}
                        </td>
                        <td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm sm:pl-0">
                          <div className="flex items-center">
                            <div>
                              <div className="font-medium text-gray-900">
                                {user.lastname}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                          <div className="text-gray-900">
                            {user.email_address}
                          </div>
                        </td>
                        {showOrganization && (
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                            <div className="text-gray-900">
                              {user.organisation_name}
                            </div>
                          </td>
                        )}
                        {isPendingUsers && (
                          <td className="flex gap-3 whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                            <button
                              type="button"
                              className="inline-flex items-center rounded bg-green-700 px-2 py-1 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-green-600 hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-green-700"
                              onClick={() =>
                                handleAction &&
                                handleAction(
                                  user.user_invitation_id
                                    ? user.user_invitation_id
                                    : null,
                                  "accept"
                                )
                              }
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center rounded bg-red-400 px-2 py-1 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-red-600 hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-red-400"
                              onClick={() =>
                                handleAction &&
                                handleAction(
                                  user.user_invitation_id
                                    ? user.user_invitation_id
                                    : null,
                                  "reject"
                                )
                              }
                            >
                              Reject
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTable;
